import { useCallback, useEffect, useState } from "react";
import { Layers, FlaskConical, RotateCcw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { Pagination } from "@/components/Pagination";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { RefBadge } from "@/components/RefBadge";

/** Workspace Recycle Bin — lists soft-deleted suites + tests. Each row offers
 * Restore (puts the entity back into normal listings) or Delete permanently
 * (hard-delete; run history is also purged for tests). Suite restore restores
 * the cascade-deleted tests too. */
export default function WorkspaceBinPage() {
  const confirm = useConfirm();
  const [tab, setTab] = useState("tests");

  const [tests, setTests] = useState(null);
  const [testsTotal, setTestsTotal] = useState(0);
  const [testsPage, setTestsPage] = useState(1);

  const [suites, setSuites] = useState(null);
  const [suitesTotal, setSuitesTotal] = useState(0);
  const [suitesPage, setSuitesPage] = useState(1);

  const PER_PAGE = 50;
  const [busyId, setBusyId] = useState(null);

  const loadTests = useCallback(() => {
    api.get(`/bin/tests?page=${testsPage}&per_page=${PER_PAGE}`)
      .then((r) => {
        setTests(r.data.items || []);
        setTestsTotal(r.data.total || 0);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [testsPage]);

  const loadSuites = useCallback(() => {
    api.get(`/bin/suites?page=${suitesPage}&per_page=${PER_PAGE}`)
      .then((r) => {
        setSuites(r.data.items || []);
        setSuitesTotal(r.data.total || 0);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [suitesPage]);

  useEffect(loadTests, [loadTests]);
  useEffect(loadSuites, [loadSuites]);

  const handleRestoreTest = async (t) => {
    setBusyId(t.id);
    try {
      await api.post(`/tests/${t.id}/restore`);
      toast.success("Test restored");
      loadTests();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handlePurgeTest = async (t) => {
    const ok = await confirm({
      title: "Permanently delete this test?",
      description: `"${t.name}" and all of its run history will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete forever",
      danger: true,
      testId: "confirm-purge-test",
    });
    if (!ok) return;
    setBusyId(t.id);
    try {
      await api.delete(`/tests/${t.id}/permanent`);
      toast.success("Test deleted permanently");
      loadTests();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleRestoreSuite = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/suites/${s.id}/restore`);
      toast.success("Suite restored");
      loadSuites();
      loadTests();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handlePurgeSuite = async (s) => {
    const ok = await confirm({
      title: "Permanently delete this suite?",
      description: `"${s.name}" and ${s.cascaded_test_count} cascade-deleted test(s) will be permanently removed (along with their run history). This cannot be undone.`,
      confirmLabel: "Delete forever",
      danger: true,
      testId: "confirm-purge-suite",
    });
    if (!ok) return;
    setBusyId(s.id);
    try {
      await api.delete(`/suites/${s.id}/permanent`);
      toast.success("Suite deleted permanently");
      loadSuites();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const testColumns = [
    {
      key: "name",
      label: "Test name",
      mobileVariant: "title",
      render: (t) => (
        <div className="flex flex-col">
          <RefBadge value={t.ref} testId={`bin-test-ref-${t.id}`} />
          <span className="rt-primary mt-1" data-testid={`bin-test-name-${t.id}`}>{t.name}</span>
          {t.suite_name && (
            <span className="text-[11px] text-[#94A3B8] mt-0.5">
              From suite: <span className="font-medium text-[#64748B]">{t.suite_name}</span>
              {t.parent_suite_deleted && <span className="ml-1 text-[#B45309]">(suite is also in bin)</span>}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "target",
      label: "Target URL",
      render: (t) => (
        <span className="text-xs text-[#64748B] font-mono">
          {t.target_url.replace(/^https?:\/\//, "").slice(0, 36)}
        </span>
      ),
    },
    {
      key: "deleted",
      label: "Moved to bin",
      render: (t) => (
        <span className="text-xs text-[#64748B]" data-testid={`bin-test-deleted-at-${t.id}`}>
          {formatDateTime(t.deleted_at)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (t) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleRestoreTest(t)}
            disabled={busyId === t.id || t.parent_suite_deleted}
            data-testid={`bin-restore-test-${t.id}`}
            title={t.parent_suite_deleted ? "Restore the parent suite first" : "Restore"}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#E5E7EB] text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busyId === t.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Restore
          </button>
          <button
            onClick={() => handlePurgeTest(t)}
            disabled={busyId === t.id}
            data-testid={`bin-purge-test-${t.id}`}
            title="Delete permanently"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#FCA5A5] hover:bg-[#FEF2F2] disabled:opacity-50"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  const suiteColumns = [
    {
      key: "name",
      label: "Suite name",
      mobileVariant: "title",
      render: (s) => (
        <div className="flex flex-col">
          <RefBadge value={s.ref} testId={`bin-suite-ref-${s.id}`} />
          <span className="rt-primary mt-1" data-testid={`bin-suite-name-${s.id}`}>{s.name}</span>
          {s.description && (
            <span className="text-[11px] text-[#94A3B8] mt-0.5">{s.description}</span>
          )}
        </div>
      ),
    },
    {
      key: "cascaded",
      label: "Cascaded tests",
      render: (s) => (
        <span className="text-xs text-[#475569]" data-testid={`bin-suite-cascaded-${s.id}`}>
          {s.cascaded_test_count}
        </span>
      ),
    },
    {
      key: "deleted",
      label: "Moved to bin",
      render: (s) => (
        <span className="text-xs text-[#64748B]" data-testid={`bin-suite-deleted-at-${s.id}`}>
          {formatDateTime(s.deleted_at)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (s) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleRestoreSuite(s)}
            disabled={busyId === s.id}
            data-testid={`bin-restore-suite-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#E5E7EB] text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-50"
          >
            {busyId === s.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Restore
          </button>
          <button
            onClick={() => handlePurgeSuite(s)}
            disabled={busyId === s.id}
            data-testid={`bin-purge-suite-${s.id}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#FCA5A5] hover:bg-[#FEF2F2] disabled:opacity-50"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  const Tab = ({ id, icon: Icon, label, count }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      data-testid={`bin-tab-${id}`}
      className={`inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium transition-colors ${
        tab === id
          ? "bg-[#0F172A] text-white"
          : "text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
      }`}
    >
      <Icon size={13} />
      {label}
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === id ? "bg-white/15" : "bg-[#E2E8F0] text-[#475569]"}`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="workspace-bin-page">
      <PageHeader
        title="Recycle Bin"
        description="Deleted suites and tests stay here. Restore them to bring them back, or delete permanently to remove all traces (including run history)."
      />

      <div className="flex items-center gap-1 p-1 bg-[#F8FAFC] border border-[#E5E7EB] rounded-md w-fit mb-6">
        <Tab id="tests" icon={FlaskConical} label="Tests" count={testsTotal} />
        <Tab id="suites" icon={Layers} label="Suites" count={suitesTotal} />
      </div>

      {tab === "tests" ? (
        tests === null ? (
          <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
        ) : tests.length === 0 ? (
          <div className="surface-card p-12 text-center" data-testid="bin-empty-tests">
            <FlaskConical size={24} className="mx-auto mb-3 text-[#94A3B8]" />
            <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">Nothing here</h2>
            <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
              Deleted tests will land in this bin. Restore any time within 30 days.
            </p>
          </div>
        ) : (
          <>
            <ResponsiveTable
              testId="bin-tests-table"
              columns={testColumns}
              rows={tests}
              getRowTestId={(t) => `bin-test-row-${t.id}`}
            />
            <Pagination
              page={testsPage}
              perPage={PER_PAGE}
              total={testsTotal}
              onChange={setTestsPage}
              testId="bin-tests-pagination"
            />
          </>
        )
      ) : suites === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : suites.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="bin-empty-suites">
          <Layers size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">Nothing here</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Deleted suites will land in this bin. Restore any time within 30 days.
          </p>
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="bin-suites-table"
            columns={suiteColumns}
            rows={suites}
            getRowTestId={(s) => `bin-suite-row-${s.id}`}
          />
          <Pagination
            page={suitesPage}
            perPage={PER_PAGE}
            total={suitesTotal}
            onChange={setSuitesPage}
            testId="bin-suites-pagination"
          />
        </>
      )}
    </div>
  );
}
